import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as loginApi, logout as logoutApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Cross-tab identity sync. A `storage` event fires only in OTHER tabs of the same
  // browser when localStorage changes. If someone logs in as a different user (or logs
  // out) in another tab, every open tab shares that one token — so this tab would keep
  // rendering the old badge over a now-different live token (the stale-badge / live-token
  // split that mis-reads as a permissions error). React to any change of the token key:
  // gone → drop to login; different value → hard reload so the whole app, badge included,
  // re-reads the new identity.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'token') return;          // only the auth token matters
      if (e.oldValue === e.newValue) return;  // no real change (e.g. same-user re-login)
      if (!e.newValue) { window.location.href = '/login'; return; }  // logged out elsewhere
      window.location.reload();               // different identity → re-sync everything
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (email, password) => {
    // write-error-ok: rejects to the caller through context — Login.js awaits login() in a try and shows the error
    const res = await loginApi(email, password);
    const { token, refreshToken, user } = res.data.data;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const logout = async () => {
    // write-error-ok: a failed server-side logout must never stop the local sign-out below
    try { await logoutApi(); } catch (e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Merge fields into the current user and persist (e.g. clearing must_change_password
  // after a successful password change).
  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);